import React, { useState } from 'react';
import { Table, Result, message } from 'antd';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { hasResource } from '../../../utils/resourceAuth';
import { ResourceCode } from '../../../constants/resourceCodes';
import OperationLogFilter from './components/OperationLogFilter';
import { useOperationLogColumns } from './components/OperationLogColumns';
import TablePaginationBar from '../../../components/TablePaginationBar';
import {
    GET_SYSTEM_OPERATION_LOG_LISTING,
    GetSystemOperationLogListingData,
    GetSystemOperationLogListingVariables,
    SystemOperationLogFiltersType,
} from './graphql/queries/getSystemOperationLogListing';

const PAGE_SIZE_OPTIONS = ['10', '20', '50', '100'];

const OperationLogPage: React.FC = () => {
    const { t } = useTranslation();
    const canAccess = hasResource([ResourceCode.menu.SYSTEM_OPERATION_LOG_QUERY]);
    const columns = useOperationLogColumns();
    const [filters, setFilters] = useState<SystemOperationLogFiltersType>({});
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);

    const { data, loading } = useQuery<
        GetSystemOperationLogListingData,
        GetSystemOperationLogListingVariables
    >(GET_SYSTEM_OPERATION_LOG_LISTING, {
        variables: {
            filters,
            pageRequest: {
                page: currentPage,
                size: pageSize,
            },
        },
        fetchPolicy: 'cache-and-network',
        skip: !canAccess,
        onError: (error) => {
            message.error(t('central.parcels.messages.error') + ': ' + error.message);
        },
    });

    const handleFilterChange = (newFilters: SystemOperationLogFiltersType) => {
        setFilters(newFilters);
        setCurrentPage(1);
    };

    const handleReset = () => {
        setFilters({});
        setCurrentPage(1);
    };

    const handlePageChange = (page: number, size?: number) => {
        setCurrentPage(page);
        if (size) {
            setPageSize(size);
        }
    };

    if (!canAccess) {
        return (
            <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
                <Result status="403" title={t('common.noPermission')} />
            </div>
        );
    }

    const logs = data?.getSystemOperationLogListing?.content || [];
    const totalElements = data?.getSystemOperationLogListing?.totalElements || 0;

    return (
        <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
            <OperationLogFilter
                filters={filters}
                onFilterChange={handleFilterChange}
                onReset={handleReset}
            />
            <Table
                loading={loading}
                columns={columns}
                dataSource={logs}
                rowKey="id"
                pagination={false}
                scroll={{ x: 'max-content' }}
                size="middle"
                tableLayout="fixed"
                className="mb-4"
            />
            <TablePaginationBar
                total={totalElements}
                current={currentPage}
                pageSize={pageSize}
                onChange={handlePageChange}
                onShowSizeChange={handlePageChange}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                showSizeChanger
            />
        </div>
    );
};

export default OperationLogPage;
